import React, { useState, useContext, useEffect } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import "moment/locale/es";
import "moment/locale/pt";

// Material-UI
import {
  Typography,
  TextField,
  Tooltip,
  Button,
  Drawer,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  AppBar,
  Toolbar,
  Card,
  CardContent,
  CardHeader,
  Snackbar,
} from "@material-ui/core";
import {
  AddCircle,
  Event,
  Save,
  Edit,
  Check,
  Close,
  ArrowUpward,
  ArrowDownward,
} from "@material-ui/icons";
import ChecklistRtlIcon from "@material-ui/icons/AssignmentTurnedIn";
import { Autocomplete } from "@material-ui/lab";
import CustomLinearProgress from "../../components/CustomLinearProgress/CustomLinearProgress";
import { Alert } from "@material-ui/lab";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faKey,
  faForward,
  faCircleExclamation,
  faFlag,
  faStar,
} from "@fortawesome/free-solid-svg-icons";

//Internal components
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import TextEditor from "components/TextEditor/TextEditor.js";
import AuthContext from "../../contexts/AuthProvider.js";

/*
  Containerized component - does NOT have local state for data; it uses the values (and update functions) passed via props.
*/

const OnePageContentContainer = ({
  isCreatingOnePage,
  currentOnePage,
  lastOnePages,
  newStatus,
  setNewStatus,
  newTendency,
  setNewTendency,
  newStatusRisk,
  setNewStatusRisk,
  newDate,
  setNewDate,
  newKm,
  setNewKm,
  newAch,
  setNewAch,
  newRisks,
  setNewRisks,
  newNextSteps,
  setNewNextSteps,
  newHelpRequests,
  setNewHelpRequests,
  classes,
  t,
  selectedOnePageDate,
  renderStatusColor,
  renderRiskColor,
}) => {
  if (isCreatingOnePage) {
    return (
      <div style={{ padding: "16px 5px 0px 5px", marginBottom: "-15px" }}>
        <GridContainer spacing={2}>
          <GridItem xs={12} md={3}>
            <FormControl
              style={{ width: "100%", marginBottom: "16px" }}
              className={`${classes.selectFormControl} ${classes.selectFormControlOverride}`}
            >
              <InputLabel id="new-status-label" style={{ color: "#888" }}>
                {t("initiative_selected.one_page_status")}
              </InputLabel>
              <Select
                labelId="new-status-label"
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                label="Status"
                displayEmpty
                classes={{
                  select: `${classes.select} ${classes.selectFormControlOverride}`,
                }}
                renderValue={(selected) => {
                  const status = [
                    {
                      value: "green",
                      label: t("initiative_selected.green"),
                      color: "#4caf50",
                    },
                    {
                      value: "yellow",
                      label: t("initiative_selected.yellow"),
                      color: "#ffbb00",
                    },
                    {
                      value: "red",
                      label: t("initiative_selected.red"),
                      color: "#E04F5F",
                    },
                    {
                      value: "gray",
                      label: t("initiative_selected.gray"),
                      color: "#9e9e9e",
                    },
                  ].find((s) => s.value === selected);
                  if (!status) return "";
                  return (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <div
                        style={{
                          width: "12px",
                          height: "12px",
                          borderRadius: "50%",
                          backgroundColor: status.color,
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ color: "#777" }}>{status.label}</span>
                    </div>
                  );
                }}
              >
                {[
                  {
                    value: "green",
                    label: t("initiative_selected.green"),
                    color: "#4caf50",
                  },
                  {
                    value: "yellow",
                    label: t("initiative_selected.yellow"),
                    color: "#ffbb00",
                  },
                  {
                    value: "red",
                    label: t("initiative_selected.red"),
                    color: "#E04F5F",
                  },
                  {
                    value: "gray",
                    label: t("initiative_selected.gray"),
                    color: "#9e9e9e",
                  },
                ].map((status) => (
                  <MenuItem
                    key={status.value}
                    value={status.value}
                    classes={{
                      root: classes.selectMenuItem,
                      selected: classes.selectMenuItemSelected,
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <div
                      style={{
                        width: "12px",
                        height: "12px",
                        borderRadius: "50%",
                        backgroundColor: status.color,
                        border: "0.2px solid white",
                        flexShrink: 0,
                      }}
                    />
                    <span>{status.label}</span>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </GridItem>
          {/* Tendency */}
          <GridItem xs={12} md={3}>
            <FormControl
              fullWidth
              style={{ marginBottom: "16px" }}
              className={`${classes.selectFormControl} ${classes.selectFormControlOverride}`}
            >
              <InputLabel style={{ color: "#888" }}>
                {t("initiative_selected.one_page_tendency")}
              </InputLabel>
              <Select
                value={newTendency}
                onChange={(e) => setNewTendency(e.target.value)}
                displayEmpty
                classes={{
                  select: `${classes.select} ${classes.selectFormControlOverride}`,
                }}
              >
                <MenuItem
                  value="UP"
                  classes={{
                    root: classes.selectMenuItem,
                    selected: classes.selectMenuItemSelected,
                  }}
                >
                  {t("initiative_selected.tendency_up")}
                </MenuItem>
                <MenuItem
                  value="DOWN"
                  classes={{
                    root: classes.selectMenuItem,
                    selected: classes.selectMenuItemSelected,
                  }}
                  style={{
                    alignItems: "center",
                  }}
                >
                  {t("initiative_selected.tendency_down")}
                </MenuItem>
              </Select>
            </FormControl>
          </GridItem>
          <GridItem xs={12} md={3}>
            <FormControl
              style={{ width: "100%", marginBottom: "16px" }}
              className={`${classes.selectFormControl} ${classes.selectFormControlOverride}`}
            >
              <InputLabel id="new-status_risk-label" style={{ color: "#888" }}>
                {t("initiative_selected.one_page_status_risk")}
              </InputLabel>
              <Select
                labelId="new-status_risk-label"
                value={newStatusRisk}
                onChange={(e) => setNewStatusRisk(e.target.value)}
                label="Status Risk"
                displayEmpty
                classes={{
                  select: `${classes.select} ${classes.selectFormControlOverride}`,
                }}
                renderValue={(selected) => {
                  const status_risk = [
                    {
                      value: "green",
                      label: t("initiative_selected.green"),
                      color: "#4caf50",
                    },
                    {
                      value: "yellow",
                      label: t("initiative_selected.yellow"),
                      color: "#ffbb00",
                    },
                    {
                      value: "red",
                      label: t("initiative_selected.red"),
                      color: "#E04F5F",
                    },
                    {
                      value: "gray",
                      label: t("initiative_selected.gray"),
                      color: "#9e9e9e",
                    },
                  ].find((s) => s.value === selected);
                  if (!status_risk) return "";
                  return (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <div
                        style={{
                          width: "12px",
                          height: "12px",
                          borderRadius: "50%",
                          backgroundColor: status_risk.color,
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ color: "#777" }}>{status_risk.label}</span>
                    </div>
                  );
                }}
              >
                {[
                  {
                    value: "green",
                    label: t("initiative_selected.green"),
                    color: "#4caf50",
                  },
                  {
                    value: "yellow",
                    label: t("initiative_selected.yellow"),
                    color: "#ffbb00",
                  },
                  {
                    value: "red",
                    label: t("initiative_selected.red"),
                    color: "#E04F5F",
                  },
                  {
                    value: "gray",
                    label: t("initiative_selected.gray"),
                    color: "#9e9e9e",
                  },
                ].map((status_risk) => (
                  <MenuItem
                    key={status_risk.value}
                    value={status_risk.value}
                    classes={{
                      root: classes.selectMenuItem,
                      selected: classes.selectMenuItemSelected,
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <div
                      style={{
                        width: "12px",
                        height: "12px",
                        borderRadius: "50%",
                        backgroundColor: status_risk.color,
                        border: "0.2px solid white",
                        flexShrink: 0,
                      }}
                    />
                    <span>{status_risk.label}</span>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </GridItem>
          <GridItem xs={12} md={3}>
            <TextField
              label={t("initiative_selected.date")}
              type="date"
              fullWidth
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              classes={{ root: classes.textFieldRoot }}
              InputProps={{
                classes: { input: classes.inputText },
                style: { fontSize: "14px" },
              }}
              InputLabelProps={{
                classes: { root: classes.labelText },
                shrink: true,
              }}
              style={{ marginBottom: "16px" }}
            />
          </GridItem>
        </GridContainer>

        <GridContainer spacing={2}>
          {/* KEY MESSAGES */}
          <GridItem xs={12} md={12}>
            <Card className={classes.cardOnePage} elevation={0}>
              <CardHeader
                title={
                  <>
                    <FontAwesomeIcon
                      icon={faKey}
                      style={{ marginRight: "8px" }}
                    />
                    {t("initiative_selected.one_page_key_messages")}
                  </>
                }
                style={{ backgroundColor: "#fff", marginBottom: "-3px" }}
                titleTypographyProps={{
                  style: {
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "-30px",
                    borderBottom: "1px solid #000",
                    paddingBottom: "5px",
                  },
                }}
              />
              <CardContent>
                <TextEditor
                  initialContent={newKm}
                  setContent={setNewKm}
                  readOnly={false}
                  editorStyle={{
                    backgroundColor: "#fff",
                    maxHeight: "90px",
                    minHeight: "90px",
                    overflowY: "auto",
                    padding: "10px",
                    border: "none",
                    outline: "none",
                    boxShadow: "none",
                  }}
                />
              </CardContent>
            </Card>
          </GridItem>

          {/* POINTS */}
          <GridItem xs={12} md={12}>
            <Card className={classes.cardOnePage} elevation={0}>
              <CardHeader
                title={
                  <>
                    <FontAwesomeIcon
                      icon={faCircleExclamation}
                      style={{ marginRight: "8px" }}
                    />
                    {t("initiative_selected.one_page_points_discussion")}
                  </>
                }
                style={{ backgroundColor: "#fff", marginBottom: "-3px" }}
                titleTypographyProps={{
                  style: {
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "-30px",
                    borderBottom: "1px solid #000",
                    paddingBottom: "5px",
                  },
                }}
              />
              <CardContent>
                <TextEditor
                  key={`helpRequests-${setNewHelpRequests}`}
                  initialContent={newHelpRequests}
                  setContent={setNewHelpRequests}
                  readOnly={false}
                  editorStyle={{
                    backgroundColor: "#fff",
                    maxHeight: "110px",
                    minHeight: "110px",
                    overflowY: "auto",
                    padding: "10px",
                    border: "none",
                    outline: "none",
                    boxShadow: "none",
                  }}
                />
              </CardContent>
            </Card>
          </GridItem>

          {/* RISK */}
          <GridItem xs={12} md={12}>
            <Card className={classes.cardOnePage} elevation={0}>
              <CardHeader
                title={
                  <>
                    <FontAwesomeIcon
                      icon={faCircleExclamation}
                      style={{ marginRight: "8px" }}
                    />
                    {t("initiative_selected.one_page_risks")}
                  </>
                }
                style={{ backgroundColor: "#fff", marginBottom: "-3px" }}
                titleTypographyProps={{
                  style: {
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "-30px",
                    borderBottom: "1px solid #000",
                    paddingBottom: "5px",
                  },
                }}
              />
              <CardContent>
                <TextEditor
                  key={`editor-risks-${setNewRisks}`}
                  initialContent={newRisks}
                  setContent={setNewRisks}
                  readOnly={false}
                  editorStyle={{
                    backgroundColor: "#fff",
                    maxHeight: "110px",
                    minHeight: "110px",
                    overflowY: "auto",
                    padding: "10px",
                    border: "none",
                    outline: "none",
                    boxShadow: "none",
                  }}
                />
              </CardContent>
            </Card>
          </GridItem>

          {/* NEXT STEPS */}
          <GridItem xs={12} md={6}>
            <Card className={classes.cardOnePage} elevation={0}>
              <CardHeader
                title={
                  <>
                    <FontAwesomeIcon
                      icon={faForward}
                      style={{ marginRight: "8px" }}
                    />
                    {t("initiative_selected.one_page_next_steps")}
                  </>
                }
                style={{ backgroundColor: "#fff", marginBottom: "-3px" }}
                titleTypographyProps={{
                  style: {
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "-30px",
                    borderBottom: "1px solid #000",
                    paddingBottom: "5px",
                  },
                }}
              />
              <CardContent>
                <TextEditor
                  key={`editor-Next-${setNewNextSteps}`}
                  initialContent={newNextSteps}
                  setContent={setNewNextSteps}
                  readOnly={false}
                  editorStyle={{
                    backgroundColor: "#fff",
                    maxHeight: "110px",
                    minHeight: "110px",
                    overflowY: "auto",
                    padding: "10px",
                    border: "none",
                    outline: "none",
                    boxShadow: "none",
                  }}
                />
              </CardContent>
            </Card>
          </GridItem>

          {/* ACHIEVEMENTS */}
          <GridItem xs={12} md={6}>
            <Card className={classes.cardOnePage} elevation={0}>
              <CardHeader
                title={
                  <>
                    <FontAwesomeIcon
                      icon={faStar}
                      style={{ marginRight: "8px" }}
                    />
                    {t("initiative_selected.one_page_achievements")}
                  </>
                }
                style={{ backgroundColor: "#fff", marginBottom: "-3px" }}
                titleTypographyProps={{
                  style: {
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "-30px",
                    borderBottom: "1px solid #000",
                    paddingBottom: "5px",
                  },
                }}
              />
              <CardContent>
                <TextEditor
                  key={`editor-Ach-${setNewAch}`}
                  initialContent={newAch}
                  setContent={setNewAch}
                  readOnly={false}
                  editorStyle={{
                    backgroundColor: "#fff",
                    maxHeight: "90px",
                    minHeight: "90px",
                    overflowY: "auto",
                    padding: "10px",
                    border: "none",
                    outline: "none",
                    boxShadow: "none",
                  }}
                />
              </CardContent>
            </Card>
          </GridItem>
        </GridContainer>
      </div>
    );
  }
  // Mode readonly – uses data from currentOnePage
  if (currentOnePage) {
    return (
      <div style={{ paddingTop: "16px", marginBottom: "-15px" }}>
        <GridContainer spacing={2} style={{ marginBottom: "10px" }}>
          {/* Initiative Leader (Sempre alinhado à esquerda) */}
          <GridItem xs={12} md={4} style={{ textAlign: "left" }}>
            <div style={{ fontSize: "14px", color: "#333" }}>
              <strong>
                {t("initiative_selected.one_page_initiative_leader")}:{" "}
              </strong>
              {currentOnePage.initiative_leader || ""}
            </div>
          </GridItem>

          {/* Initiative Sponsor (Centralizado a partir de md, mas à esquerda em xs/sm) */}
          <GridItem xs={12} md={4}>
            <div
              style={{
                fontSize: "14px",
                color: "#333",
                width: "100%", // Faz o texto ocupar toda a largura do GridItem
                textAlign: window.innerWidth >= 960 ? "center" : "left", // Alinha centro em md+ (>= 960px), senão à esquerda
              }}
            >
              <strong>
                {t("initiative_selected.one_page_initiative_sponser")}:{" "}
              </strong>
              {currentOnePage.initiative_sponser || ""}
            </div>
          </GridItem>

          {/* One Page Editor (Justificado à direita em md+, mas à esquerda em xs/sm) */}
          <GridItem xs={12} md={4}>
            <div
              style={{
                fontSize: "14px",
                color: "#333",
                width: "100%", // Ocupa toda a largura para permitir alinhamento à direita
                textAlign: window.innerWidth >= 960 ? "right" : "left", // Alinha à direita em md+ (>= 960px), senão à esquerda
              }}
            >
              <strong>{t("initiative_selected.one_page_editor")}: </strong>
              {currentOnePage.one_page_editor || ""}
            </div>
          </GridItem>
        </GridContainer>

        <GridContainer spacing={2}>
          <GridItem
            xs={12}
            md={3}
            style={{ display: "flex", justifyContent: "center" }}
          >
            <Card
              className={classes.cardOnePage}
              elevation={0}
              style={{ width: "100%" }}
            >
              <CardHeader
                title={
                  <>
                    <FontAwesomeIcon
                      icon={faFlag}
                      style={{ marginRight: "8px" }}
                    />
                    {t("initiative_selected.one_page_status")}
                  </>
                }
                style={{ backgroundColor: "#fff", marginBottom: "-3px" }}
                titleTypographyProps={{
                  style: {
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "-30px",
                    borderBottom: "1px solid #000",
                    paddingBottom: "5px",
                  },
                }}
              />
              <CardContent>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    style={{
                      color: "#555",
                      fontSize: "11px",
                      marginTop: "5px",
                    }}
                  >
                    {moment(selectedOnePageDate).startOf("day").fromNow()}
                  </Typography>

                  {/* Horizontal Container:Big circle + (max 3) mini-circles*/}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    {/* Container mini-circles */}
                    <div style={{ display: "flex", gap: "4px" }}>
                      {(lastOnePages || [])
                        .slice(0, 3) // pega até 3 das mais antigas
                        .map((page, idx) => (
                          <div
                            key={idx}
                            style={{
                              width: "15px",
                              height: "15px",
                              borderRadius: "50%",
                              backgroundColor: renderStatusColor(
                                page.status || "gray",
                              ),
                            }}
                          />
                        ))}
                    </div>
                    {/* BIG BALL MAIN (UNE PAGE CURRENT) */}
                    <div
                      style={{
                        width: "60px",
                        height: "60px",
                        borderRadius: "50%",
                        backgroundColor: renderStatusColor(
                          currentOnePage?.status || "gray",
                        ),
                      }}
                    />
                    {/* The trend arrow*/}
                    {currentOnePage.tendency === "UP" ? (
                      <ArrowUpward style={{ fontSize: 22, color: "#4caf50" }} />
                    ) : currentOnePage.tendency === "DOWN" ? (
                      <ArrowDownward
                        style={{ fontSize: 22, color: "#E04F5F" }}
                      />
                    ) : null}
                  </div>
                </div>
              </CardContent>
            </Card>
          </GridItem>

          {/* KEY MESSAGES */}
          <GridItem xs={12} md={9}>
            <Card className={classes.cardOnePage} elevation={0}>
              <CardHeader
                title={
                  <>
                    <FontAwesomeIcon
                      icon={faKey}
                      style={{ marginRight: "8px" }}
                    />
                    {t("initiative_selected.one_page_key_messages")}
                  </>
                }
                style={{ backgroundColor: "#fff", marginBottom: "-3px" }}
                titleTypographyProps={{
                  style: {
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "-30px",
                    borderBottom: "1px solid #000",
                    paddingBottom: "5px",
                  },
                }}
              />
              <CardContent>
                <TextEditor
                  initialContent={currentOnePage.km}
                  setContent={() => {}}
                  readOnly
                  editorStyle={{
                    backgroundColor: "#fff",
                    maxHeight: "90px",
                    minHeight: "90px",
                    overflowY: "auto",
                    padding: "10px",
                    border: "none",
                    outline: "none",
                    boxShadow: "none",
                  }}
                />
              </CardContent>
            </Card>
          </GridItem>

          {/* POINTS */}
          <GridItem xs={12} md={12}>
            <Card className={classes.cardOnePage} elevation={0}>
              <CardHeader
                title={
                  <>
                    <FontAwesomeIcon
                      icon={faCircleExclamation}
                      style={{ marginRight: "8px" }}
                    />
                    {t("initiative_selected.one_page_points_discussion")}
                  </>
                }
                style={{ backgroundColor: "#fff", marginBottom: "-3px" }}
                titleTypographyProps={{
                  style: {
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "-30px",
                    borderBottom: "1px solid #000",
                    paddingBottom: "5px",
                  },
                }}
              />
              <CardContent>
                <TextEditor
                  initialContent={currentOnePage.help_requests}
                  setContent={() => {}}
                  readOnly
                  editorStyle={{
                    backgroundColor: "#fff",
                    maxHeight: "110px",
                    minHeight: "110px",
                    overflowY: "auto",
                    padding: "10px",
                    border: "none",
                    outline: "none",
                    boxShadow: "none",
                  }}
                />
              </CardContent>
            </Card>
          </GridItem>

          {/* RISKS */}
          <GridItem xs={12} md={12}>
            <Card className={classes.cardOnePage} elevation={0}>
              <CardHeader
                title={
                  <div style={{ display: "flex" }}>
                    <div
                      style={{
                        marginRight: "7px",
                        marginTop: "5x",
                        width: "15px",
                        height: "15px",
                        borderRadius: "50%",
                        backgroundColor: renderRiskColor(
                          currentOnePage?.status_risk || "gray",
                        ),
                      }}
                    />
                    {t("initiative_selected.one_page_risks")}
                  </div>
                }
                style={{ backgroundColor: "#fff", marginBottom: "-3px" }}
                titleTypographyProps={{
                  style: {
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "-30px",
                    borderBottom: "1px solid #000",
                    paddingBottom: "5px",
                  },
                }}
              />
              <CardContent>
                <TextEditor
                  initialContent={currentOnePage.risks}
                  setContent={() => {}}
                  readOnly
                  editorStyle={{
                    backgroundColor: "#fff",
                    maxHeight: "110px",
                    minHeight: "110px",
                    overflowY: "auto",
                    padding: "10px",
                    border: "none",
                    outline: "none",
                    boxShadow: "none",
                  }}
                />
              </CardContent>
            </Card>
          </GridItem>

          {/* NEXT STEPS */}
          <GridItem xs={12} md={6}>
            <Card className={classes.cardOnePage} elevation={0}>
              <CardHeader
                title={
                  <>
                    <FontAwesomeIcon
                      icon={faForward}
                      style={{ marginRight: "8px" }}
                    />
                    {t("initiative_selected.one_page_next_steps")}
                  </>
                }
                style={{ backgroundColor: "#fff", marginBottom: "-3px" }}
                titleTypographyProps={{
                  style: {
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "-30px",
                    borderBottom: "1px solid #000",
                    paddingBottom: "5px",
                  },
                }}
              />
              <CardContent>
                <TextEditor
                  initialContent={currentOnePage.next_steps}
                  setContent={() => {}}
                  readOnly
                  editorStyle={{
                    backgroundColor: "#fff",
                    maxHeight: "110px",
                    minHeight: "110px",
                    overflowY: "auto",
                    padding: "10px",
                    border: "none",
                    outline: "none",
                    boxShadow: "none",
                  }}
                />
              </CardContent>
            </Card>
          </GridItem>

          {/* ACHIEVEMENTS */}
          <GridItem xs={12} md={6}>
            <Card className={classes.cardOnePage} elevation={0}>
              <CardHeader
                title={
                  <>
                    <FontAwesomeIcon
                      icon={faStar}
                      style={{ marginRight: "8px" }}
                    />
                    {t("initiative_selected.one_page_achievements")}
                  </>
                }
                style={{ backgroundColor: "#fff", marginBottom: "-3px" }}
                titleTypographyProps={{
                  style: {
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "-30px",
                    borderBottom: "1px solid #000",
                    paddingBottom: "5px",
                  },
                }}
              />
              <CardContent>
                <TextEditor
                  initialContent={currentOnePage.achievements}
                  setContent={() => {}}
                  readOnly
                  editorStyle={{
                    backgroundColor: "#fff",
                    maxHeight: "90px",
                    minHeight: "90px",
                    overflowY: "auto",
                    padding: "10px",
                    border: "none",
                    outline: "none",
                    boxShadow: "none",
                  }}
                />
              </CardContent>
            </Card>
          </GridItem>
        </GridContainer>
      </div>
    );
  }
  return (
    <Typography
      variant="body1"
      style={{
        color: "#555",
        marginTop: "16px",
        textAlign: "center",
        fontStyle: "italic",
      }}
    >
      {t("initiative_selected.one_page_no_data")}
    </Typography>
  );
};

//////////////////////////////////////////////////////////////////
// Main component OnePage
//////////////////////////////////////////////////////////////////
const OnePage = ({
  selectedInitiative,
  selectedOnePageDate,
  setSelectedOnePageDate,
  onePageInfoMock,
  onePageHistoryMock,
  onePageHasApproval,
  t,
  classes,
  showContent = false,
}) => {
  /*
  console.log("QUINAUD - teste dados recebidos");
  console.log(onePageInfoMock);
  console.log("selected initiative id:" + selectedInitiative?.id);
  console.log("selected onepage:" + selectedOnePageDate);
  */
  // Checks the current one page
  // QUINAUD - original passed: info.date === selectedOnePageDate && info.id === selectedInitiative?.id,
  const currentOnePage = onePageInfoMock.find(
    (info) => info.date === "2025-03-10" && info.id === 1,
  );
  const { userDetails } = useContext(AuthContext);
  // console.log("OnePage - userDetails:", userDetails);

  const currentLanguage = t(t);
  moment.locale(currentLanguage);

  //QUINAUD - original text: {t("initiative_selected.one_page_no_data")}
  if (!currentOnePage) {
    return (
      <Typography /* ... */>
        <p>Teste Erro</p>
      </Typography>
    );
  }
  //generic example of "onepages" array containing {date, status, ...}
  const sortedOnePages = onePageInfoMock
    .filter((op) => op.id === currentOnePage.id)
    .filter((op) => op.date < currentOnePage.date) // ou se basear no ID, etc.
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  //Take the last 3
  // eslint-disable-next-line no-unused-vars
  const lastOnePages = sortedOnePages.slice(-3);

  // Formata a data conforme o idioma selecionado

  // Function to set the Alert color based on the approved field
  const getApprovedColor = (approved) => {
    switch (approved) {
      case "yes":
        return "#d0e9c6";
      case "no":
        return "#faf2cc";
      case "pending":
      default:
        return "#eee";
    }
  };

  // States for creating/editing OnePage (raised in the main component)
  const [isCreatingOnePage, setIsCreatingOnePage] = useState(false);
  const [isEditingOnePage, setIsEditingOnePage] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [selectedApproval, setSelectedApproval] = useState(null);

  const [newStatus, setNewStatus] = useState("");
  const [newTendency, setNewTendency] = useState("");
  const [newStatusRisk, setNewStatusRisk] = useState("");
  const [newDate, setNewDate] = useState(moment().format("YYYY-MM-DD"));
  const [newKm, setNewKm] = useState("");
  const [newAch, setNewAch] = useState("");
  const [newNextSteps, setNewNextSteps] = useState("");
  const [newRisks, setNewRisks] = useState("");
  const [newHelpRequests, setNewHelpRequests] = useState("");

  // Drawer - Approvals
  const [isApproveDrawerOpen, setIsApproveDrawerOpen] = useState(false);
  const [approveComment, setApproveComment] = useState("");

  // Approver Floating Notification
  const [openApproverNotification, setOpenApproverNotification] =
    useState(true);
  const [progress, setProgress] = useState(100);

  // Cancel dialog
  const handleOpenCancelDialog = () => setIsCancelDialogOpen(true);
  const handleCloseCancelDialog = () => setIsCancelDialogOpen(false);
  const handleConfirmCancel = () => {
    setIsCreatingOnePage(false);
    setIsEditingOnePage(false);
    setIsCancelDialogOpen(false);
  };

  //Creation/Edition Functions
  const handleCreateOnePage = () => {
    setIsCreatingOnePage(true);
    setIsEditingOnePage(false);
    setNewStatus("");
    setNewTendency("");
    setNewStatusRisk("");
    setNewAch("");
    setNewDate(moment().format("YYYY-MM-DD"));
    setNewKm("");
    setNewNextSteps("");
    setNewRisks("");
    setNewHelpRequests("");
  };

  const handleEditOnePage = () => {
    if (currentOnePage) {
      setNewStatus(currentOnePage.status);
      setNewTendency(currentOnePage.tendency);
      setNewStatusRisk(currentOnePage.status_risk);
      setNewDate(currentOnePage.date);
      setNewKm(currentOnePage.km);
      setNewAch(currentOnePage.achievements);
      setNewNextSteps(currentOnePage.next_steps);
      setNewRisks(currentOnePage.risks);
      setNewHelpRequests(currentOnePage.help_requests);
      setIsEditingOnePage(true);
      setIsCreatingOnePage(true);
    }
  };

  const handleSaveNewOnePage = () => {
    if (!newStatus) {
      // console.log("Selecione um status antes de salvar.");
      return;
    }
    /*
    const onePageData = {
      id: selectedInitiative.id,
      status: newStatus,
      status_risk: newStatusRisk,
      tendency: newTendency,
      date: newDate,
      km: newKm,
      next_steps: newNextSteps,
      risks: newRisks,
      help_requests: newHelpRequests,
    };
    */
    if (isEditingOnePage) {
      // console.log("Edited One Page:", onePageData);
    } else {
      // console.log("New One Page:", onePageData);
    }
    setIsCreatingOnePage(false);
    setIsEditingOnePage(false);
  };

  // Approval Drawer Functions
  const handleOpenApproveDrawer = () => {
    if (isCreatingOnePage) return;
    setIsApproveDrawerOpen(true);
  };
  const handleCloseApproveDrawer = () => {
    setIsApproveDrawerOpen(false);
  };
  const handleApproveSave = () => {
    // console.log("Aprovar/Reprovar c/ comentário:", approveComment);
    setIsApproveDrawerOpen(false);
    setApproveComment("");
  };

  // Updates notification progress bar (3 seconds)
  useEffect(() => {
    if (openApproverNotification && onePageHasApproval.has_approval === "yes") {
      const interval = 100;
      const decrement = 100 / (3000 / interval);
      const timer = setInterval(() => {
        setProgress((prev) => {
          const next = prev - decrement;
          if (next <= 0) {
            clearInterval(timer);
            return 0;
          }
          return next;
        });
      }, interval);
      return () => clearInterval(timer);
    }
  }, [openApproverNotification]);

  // Condition to define if the user is an approver
  const isApprover =
    selectedInitiative.leader === userDetails.email ||
    ["TO", "IMG", "DEV"].includes(userDetails.user_role);

  const renderStatusColor = (status) => {
    switch (status) {
      case "red":
        return "#E04F5F";
      case "green":
        return "#4caf50";
      case "yellow":
        return "#ffbb00";
      case "gray":
      default:
        return "#9e9e9e";
    }
  };

  const renderRiskColor = (status_risk) => {
    switch (status_risk) {
      case "red":
        return "#E04F5F";
      case "green":
        return "#4caf50";
      case "yellow":
        return "#ffbb00";
      case "gray":
      default:
        return "#9e9e9e";
    }
  };
  // console.log("QUINAUD - variavel showContent:");
  // console.log(showContent);

  // If showContent is false, render only containerized content in readonly mode
  if (!showContent) {
    if (currentOnePage) {
      return (
        <div style={{ padding: "16px 5px", marginBottom: "-15px" }}>
          <OnePageContentContainer
            isCreatingOnePage={false}
            currentOnePage={currentOnePage}
            newStatus={currentOnePage.status}
            setNewTendency={() => {}}
            newTendency={currentOnePage.tendency}
            setNewStatus={() => {}}
            newStatusRisk={currentOnePage.status_risk}
            setNewStatusRisk={() => {}}
            newDate={currentOnePage.date}
            setNewDate={() => {}}
            newKm={currentOnePage.km}
            setNewKm={() => {}}
            newAch={currentOnePage.achievements}
            setNewAch={() => {}}
            newNextSteps={currentOnePage.next_steps}
            setNewNextSteps={() => {}}
            newRisks={currentOnePage.risks}
            setNewRisks={() => {}}
            newHelpRequests={currentOnePage.help_requests}
            setNewHelpRequests={() => {}}
            classes={classes}
            t={t}
            selectedOnePageDate={selectedOnePageDate}
            renderStatusColor={renderStatusColor}
            renderRiskColor={renderRiskColor}
            lastOnePages={lastOnePages}
          />
        </div>
      );
    } else {
      return (
        <Typography
          variant="body1"
          style={{
            color: "#555",
            marginTop: "16px",
            textAlign: "center",
            fontStyle: "italic",
          }}
        >
          {t("initiative_selected.one_page_no_data")}
        </Typography>
      );
    }
  }

  // If showContent is true, render the full page:
  return (
    <div style={{ width: "100%" }}>
      {/* APP BAR Superior */}
      <AppBar
        position="static"
        color="default"
        elevation={0}
        style={{ borderRadius: "4px", border: "1px" }}
      >
        <Toolbar style={{ display: "flex", flexGrow: 1, minHeight: "48px" }}>
          <GridContainer
            alignItems="center"
            style={{ margin: 0, padding: 0 }}
            spacing={2}
          >
            <GridItem xs={12}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                {isCreatingOnePage ? (
                  <>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        marginLeft: "-12px",
                      }}
                    >
                      <Typography
                        style={{
                          fontWeight: 400,
                          marginTop: "3px",
                          fontSize: "18px",
                        }}
                      >
                        {isEditingOnePage
                          ? t("initiative_selected.one_page_editing", {
                              date: moment(newDate).format("LL"),
                            })
                          : t("initiative_selected.one_page_add_new_title")}
                      </Typography>
                    </div>
                    <div
                      style={{
                        color: "#3C4858",
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        gap: "15px",
                        marginRight: "-12px",
                      }}
                    >
                      <Tooltip title={t("initiative_selected.cancel")}>
                        <Close
                          onClick={handleOpenCancelDialog}
                          className={classes.iconButtonCustom}
                          style={{ cursor: "pointer", fontSize: "20px" }}
                        />
                      </Tooltip>
                      <Tooltip title={t("initiative_selected.save")}>
                        <Save
                          onClick={handleSaveNewOnePage}
                          className={classes.iconButtonCustom}
                          style={{ cursor: "pointer", fontSize: "20px" }}
                        />
                      </Tooltip>
                      <Dialog
                        open={isCancelDialogOpen}
                        onClose={handleCloseCancelDialog}
                        aria-labelledby="cancel-dialog-title"
                        PaperProps={{
                          style: {
                            borderRadius: 8,
                            padding: "24px",
                            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
                          },
                        }}
                      >
                        <DialogTitle
                          id="cancel-dialog-title"
                          style={{
                            fontSize: 20,
                            fontWeight: "bold",
                            textAlign: "center",
                            marginBottom: "20px",
                            marginTop: "-20px",
                          }}
                        >
                          {t("initiative_selected.cancel_confirmation_title")}
                        </DialogTitle>
                        <DialogContent
                          style={{
                            textAlign: "center",
                            marginBottom: "40px",
                            padding: 0,
                          }}
                        >
                          <Typography
                            variant="body1"
                            style={{ lineHeight: 1.6 }}
                          >
                            {t("initiative_selected.cancel_confirmation_text")}
                          </Typography>
                        </DialogContent>
                        <DialogActions
                          style={{ justifyContent: "right", padding: 0 }}
                        >
                          <Button
                            onClick={handleCloseCancelDialog}
                            style={{
                              border: "1px solid #ccc",
                              borderRadius: 8,
                              padding: "8px 15px",
                              fontWeight: "bold",
                              textTransform: "none",
                              marginRight: "12px",
                            }}
                          >
                            {t("initiative_selected.cancel_no")}
                          </Button>
                          <Button
                            onClick={handleConfirmCancel}
                            style={{
                              backgroundColor: "#d32f2f",
                              color: "#fff",
                              borderRadius: 8,
                              padding: "8px 15px",
                              fontWeight: "bold",
                              textTransform: "none",
                            }}
                          >
                            {t("initiative_selected.cancel_yes")}
                          </Button>
                        </DialogActions>
                      </Dialog>
                    </div>
                  </>
                ) : (
                  <>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        marginLeft: "-12px",
                        width: "100%",
                        boxSizing: "border-box",
                        overflow: "hidden",
                      }}
                    >
                      <Autocomplete
                        options={
                          onePageHistoryMock.find(
                            (history) => history.id === selectedInitiative.id,
                          )?.dates || []
                        }
                        getOptionLabel={(option) => moment(option).format("LL")}
                        value={selectedOnePageDate || null}
                        onChange={(event, newValue) => {
                          setSelectedOnePageDate(newValue || "");
                        }}
                        classes={{
                          root: classes.autocompleteRoot,
                          inputRoot: classes.inputRoot,
                          option: classes.option,
                          paper: classes.paper,
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Please Select..."
                            size="small"
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <Event
                                    style={{
                                      fontSize: "18px",
                                      color: "#555",
                                      marginRight: "8px",
                                    }}
                                  />
                                  {params.InputProps.startAdornment}
                                </div>
                              ),
                              classes: { input: classes.inputText },
                              style: { fontSize: "12px", paddingLeft: "0" },
                            }}
                            classes={{ root: classes.textFieldRoot }}
                            style={{ width: "220px" }}
                          />
                        )}
                        style={{ minWidth: "220px" }}
                      />
                    </div>
                    <div
                      style={{
                        color: "#3C4858",
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        gap: "10px",
                        marginRight: "-12px",
                        width: "100%",
                        boxSizing: "border-box",
                        overflowX: "hidden",
                      }}
                    >
                      {((onePageHasApproval.has_approval === "yes" &&
                        userDetails.user_role === "DEV") ||
                        userDetails.user_role === "IMG") && (
                        <Tooltip
                          title={t("initiative_selected.one_page_approve")}
                        >
                          <ChecklistRtlIcon
                            onClick={handleOpenApproveDrawer}
                            className={classes.iconButtonCustom}
                            style={{ cursor: "pointer", fontSize: "20px" }}
                          />
                        </Tooltip>
                      )}
                      <Tooltip title={t("initiative_selected.one_page_edit")}>
                        <Edit
                          onClick={handleEditOnePage}
                          className={classes.iconButtonCustom}
                          style={{ cursor: "pointer", fontSize: "20px" }}
                        />
                      </Tooltip>
                      <Tooltip
                        title={t("initiative_selected.one_page_add_new")}
                      >
                        <AddCircle
                          onClick={handleCreateOnePage}
                          className={classes.iconButtonCustom}
                          style={{ cursor: "pointer", fontSize: "20px" }}
                        />
                      </Tooltip>
                    </div>
                  </>
                )}
              </div>
            </GridItem>
          </GridContainer>
        </Toolbar>
      </AppBar>

      {/* Alert - status */}
      {onePageHasApproval.has_approval === "yes" &&
        !isCreatingOnePage &&
        currentOnePage &&
        currentOnePage.approved && (
          <Alert
            style={{
              backgroundColor: getApprovedColor(currentOnePage.approved),
              marginTop: "5px",
              marginBottom: "-3px",
              boxShadow: "none",
              minHeight: "20px",
              padding: "2px 8px",
              width: "100%",
              boxSizing: "border-box",
              overflow: "hidden",
            }}
            severity={
              currentOnePage.approved === "yes"
                ? "success"
                : currentOnePage.approved === "no"
                  ? "warning"
                  : "info"
            }
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                whiteSpace: "nowrap",
                overflow: "hidden",
                width: "100%",
              }}
            >
              <span
                style={{
                  flexShrink: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                One Page{" "}
                <span style={{ fontWeight: 600 }}>
                  {currentOnePage.approved === "yes"
                    ? "approved"
                    : currentOnePage.approved === "no"
                      ? "not approved"
                      : "pending approval"}
                </span>
              </span>
              {currentOnePage.comment && (
                <>
                  <span style={{ marginLeft: "4px", whiteSpace: "nowrap" }}>
                    {"with comment:"}
                  </span>
                  <Tooltip title={currentOnePage.comment} arrow>
                    <span
                      style={{
                        marginLeft: "4px",
                        flexShrink: 1,
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        fontStyle: "italic",
                        maxWidth: "70%",
                      }}
                    >
                      {currentOnePage.comment}
                    </span>
                  </Tooltip>
                </>
              )}
            </div>
          </Alert>
        )}

      {/* Floating notification for approver */}
      {isApprover &&
        openApproverNotification &&
        onePageHasApproval.has_approval === "yes" && (
          <Snackbar
            open={openApproverNotification}
            autoHideDuration={3000}
            onClose={() => setOpenApproverNotification(false)}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            style={{ marginTop: "8px" }}
          >
            <div
              style={{
                backgroundColor: "#f7f7f7",
                border: "1px solid #ccc",
                borderRadius: "4px",
                padding: "4px 8px",
                display: "flex",
                flexDirection: "column",
                width: "auto",
                boxSizing: "border-box",
              }}
            >
              <Typography
                variant="body2"
                style={{ color: "#555", marginBottom: "4px" }}
              >
                {t("initiative_selected.approver_flag")}
              </Typography>
              <CustomLinearProgress
                style={{ margin: "0" }}
                variant="determinate"
                color="client"
                value={progress}
              />
            </div>
          </Snackbar>
        )}

      {/* Containerized content */}
      <OnePageContentContainer
        isCreatingOnePage={isCreatingOnePage}
        currentOnePage={currentOnePage}
        newStatus={newStatus}
        setNewStatus={setNewStatus}
        newTendency={newTendency}
        setNewTendency={setNewTendency}
        newStatusRisk={newStatusRisk}
        setNewStatusRisk={setNewStatusRisk}
        newDate={newDate}
        setNewDate={setNewDate}
        newKm={newKm}
        setNewKm={setNewKm}
        newAch={newAch}
        setNewAch={setNewAch}
        newNextSteps={newNextSteps}
        setNewNextSteps={setNewNextSteps}
        newRisks={newRisks}
        setNewRisks={setNewRisks}
        newHelpRequests={newHelpRequests}
        setNewHelpRequests={setNewHelpRequests}
        classes={classes}
        t={t}
        selectedOnePageDate={selectedOnePageDate}
        renderStatusColor={renderStatusColor}
        renderRiskColor={renderRiskColor}
        lastOnePages={lastOnePages}
      />

      {/* Drawer - Approvals */}
      {onePageHasApproval.has_approval === "yes" && (
        <Drawer
          anchor="bottom"
          open={isApproveDrawerOpen}
          onClose={handleCloseApproveDrawer}
          PaperProps={{
            style: {
              height: "35vh",
              borderRadius: "7px",
              padding: "3px 10px 10px 10px",
              margin: "0vh 1vh 1vh 1vh",
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          <GridItem
            style={{
              zIndex: 1000,
              position: "absolute",
              right: "20px",
              top: "20px",
              color: "#3C4858",
            }}
          >
            <Tooltip title={t("initiative_selected.save")}>
              <Save
                onClick={handleApproveSave}
                className={classes.iconButtonCustom}
                style={{ cursor: "pointer", fontSize: "20px" }}
              />
            </Tooltip>
          </GridItem>
          <div
            style={{
              flex: 1,
              padding: "8px",
              fontSize: "0.9rem",
              transformOrigin: "top center",
            }}
            className={classes.drawerContent}
          >
            <GridContainer style={{ marginTop: "10px" }}>
              <GridItem
                xs={12}
                md={12}
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <Typography
                  variant="body1"
                  style={{
                    color: "#555",
                    fontWeight: 500,
                    marginRight: "15px",
                  }}
                >
                  {t("initiative_selected.one_page_comments_title")}
                </Typography>
                <Tooltip title={t("initiative_selected.one_page_approve")}>
                  <Check
                    className={classes.iconButtonCustom}
                    style={{
                      cursor: "pointer",
                      fontSize: "25px",
                      color: "#388e3c",
                      border:
                        selectedApproval === "approved"
                          ? "2px solid #388e3c"
                          : "none",
                      borderRadius: "50%",
                      padding: "2px",
                    }}
                    onClick={() => setSelectedApproval("approved")}
                  />
                </Tooltip>
                <Tooltip title={t("initiative_selected.one_page_disapprove")}>
                  <Close
                    className={classes.iconButtonCustom}
                    style={{
                      cursor: "pointer",
                      fontSize: "25px",
                      color: "#d32f2f",
                      border:
                        selectedApproval === "disapproved"
                          ? "2px solid #d32f2f"
                          : "none",
                      borderRadius: "50%",
                      padding: "2px",
                    }}
                    onClick={() => setSelectedApproval("disapproved")}
                  />
                </Tooltip>
              </GridItem>
              <GridItem xs={12} md={12} style={{ marginTop: "20px" }}>
                <TextField
                  label={t("initiative_selected.one_page_comment_body")}
                  multiline
                  rows={2}
                  variant="outlined"
                  fullWidth
                  placeholder={t("initiative_selected.one_page_enter_comment")}
                  value={approveComment}
                  onChange={(e) => setApproveComment(e.target.value)}
                  helperText={`${approveComment.length}/100`}
                  classes={{ root: classes.textFieldRoot }}
                  InputProps={{
                    classes: {
                      input: classes.inputText,
                      root: classes.outlinedInput,
                    },
                    style: { fontSize: "12px" },
                    inputProps: { maxLength: 100 },
                  }}
                  InputLabelProps={{
                    classes: { root: classes.labelText },
                    shrink: true,
                    style: {
                      backgroundColor: "#fff",
                      zIndex: 150,
                      padding: "0px 10px",
                      fontWeight: "700",
                      textTransform: "uppercase",
                    },
                  }}
                />
              </GridItem>
            </GridContainer>
          </div>
        </Drawer>
      )}
    </div>
  );
};

// QUINAUD - Prop Types and Default
OnePageContentContainer.propTypes = {
  isCreatingOnePage: PropTypes.bool.isRequired,
  currentOnePage: PropTypes.object,
  newStatus: PropTypes.string.isRequired,
  setNewStatus: PropTypes.func.isRequired,
  newTendency: PropTypes.string.isRequired,
  setNewTendency: PropTypes.func.isRequired,
  newStatusRisk: PropTypes.string.isRequired,
  setNewStatusRisk: PropTypes.func.isRequired,
  newDate: PropTypes.string.isRequired,
  setNewDate: PropTypes.func.isRequired,
  newKm: PropTypes.string.isRequired,
  setNewKm: PropTypes.func.isRequired,
  newAch: PropTypes.string.isRequired,
  setNewAch: PropTypes.func.isRequired,
  newNextSteps: PropTypes.string.isRequired,
  setNewNextSteps: PropTypes.func.isRequired,
  newRisks: PropTypes.string.isRequired,
  setNewRisks: PropTypes.func.isRequired,
  newHelpRequests: PropTypes.string.isRequired,
  setNewHelpRequests: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  lastOnePages: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string,
      status: PropTypes.string,
    }),
  ),
  selectedOnePageDate: PropTypes.string.isRequired,
  renderStatusColor: PropTypes.func.isRequired,
  renderRiskColor: PropTypes.func.isRequired,
};

OnePage.propTypes = {
  selectedInitiative: PropTypes.object.isRequired,
  selectedOnePageDate: PropTypes.string.isRequired,
  setSelectedOnePageDate: PropTypes.func.isRequired,
  onePageInfoMock: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      status: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
      km: PropTypes.string.isRequired,
      next_steps: PropTypes.string.isRequired,
      help_requests: PropTypes.string.isRequired,
      comment: PropTypes.string,
      approved: PropTypes.string,
    }),
  ).isRequired,
  onePageHistoryMock: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      dates: PropTypes.arrayOf(PropTypes.string).isRequired,
    }),
  ).isRequired,
  onePageHasApproval: PropTypes.shape({
    has_approval: PropTypes.oneOf(["yes", "no"]).isRequired,
  }).isRequired,
  t: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
  showContent: PropTypes.bool,
};

OnePage.defaultProps = {
  showContent: true,
};

export default OnePage;
